import { Stack, Typography } from '@mui/material';
import { ResourceType, type InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type { OptionMenuItemType } from '@/components/option-menu/option-menu';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
const CONTROLLED_BY_EON = 'Controlled by Eon (recommended)';
const LIST_OBJECTS = 'List objects';
const getInventoryLabel = (resourceType: ResourceType) => {
  switch (resourceType) {
    case ResourceType.AwsS3:
      return 'S3 inventory';
    case ResourceType.GcpCloudStorageBucket:
      return 'GCS inventory';
    default:
      return 'Inventory';
  }
};
interface ConfigField {
  displayText: string;
  menuOptions: OptionMenuItemType[];
}
export const S3BucketScanTypeField = ({
  item
}: {
  item: InventoryResource;
}) => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const {
    data: itemConfigBody,
    mutate,
    error
  } = dal.inventory.objectStoreScanMethod.useConfig(item.id);
  const {
    isAuthorized
  } = useRoles();
  const [isUpdating, setIsUpdating] = useState(false);
  const inventoryLabel = getInventoryLabel(item.resourceType);
  const handleUpdate = async ({
    enabled,
    systemControlled
  }: {
    enabled: boolean | null;
    systemControlled: boolean;
  }) => {
    if (!itemConfigBody) {
      return;
    }
    setIsUpdating(true);
    try {
      await dal.inventory.objectStoreScanMethod.update(item.id, {
        inventoryBackup: {
          ...(enabled !== null && {
            enabled
          }),
          systemControlled
        }
      });
      await mutate();
    } catch (error_0) {
      const errorMessage = await extractErrorMessage(error_0);
      snackbar.showErrorMessage(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };
  const getScanTypeField = (): ConfigField => {
    if (error) {
      return {
        displayText: 'N/A',
        menuOptions: []
      };
    }
    if (!itemConfigBody) {
      return {
        displayText: 'Loading...',
        menuOptions: []
      };
    }
    const {
      inventoryBackup
    } = itemConfigBody;
    if (!inventoryBackup || typeof inventoryBackup.systemControlled !== 'boolean') {
      return {
        displayText: 'N/A',
        menuOptions: []
      };
    }
    const isUserControlled = !inventoryBackup.systemControlled;
    const getDisplayText = () => {
      const scanType = inventoryBackup.enabled ? inventoryLabel : LIST_OBJECTS;
      if (!isUserControlled) {
        return `${scanType} (Controlled by Eon)`;
      }
      return scanType;
    };
    const isInventoryEnabled = inventoryBackup.enabled ?? undefined;
    const userControlledOptions: OptionMenuItemType[] = [{
      id: 'regular',
      text: LIST_OBJECTS,
      menuItemProps: {
        selected: isUserControlled && !isInventoryEnabled,
        disabled: !isAuthorized('update:inventory') || isUserControlled && !isInventoryEnabled,
        onClick: () => void handleUpdate({
          enabled: false,
          systemControlled: false
        })
      }
    }, {
      id: 'inventory',
      text: inventoryLabel,
      menuItemProps: {
        selected: isUserControlled && isInventoryEnabled,
        disabled: !isAuthorized('update:inventory') || isUserControlled && isInventoryEnabled,
        onClick: () => void handleUpdate({
          enabled: true,
          systemControlled: false
        })
      }
    }];
    const menuOptions: OptionMenuItemType[] = [{
      id: 'system-controlled',
      text: CONTROLLED_BY_EON,
      menuItemProps: {
        selected: !isUserControlled,
        disabled: !isAuthorized('update:inventory') || !isUserControlled,
        onClick: () => void handleUpdate({
          enabled: null,
          systemControlled: true
        })
      }
    }, ...userControlledOptions];
    return {
      displayText: getDisplayText(),
      menuOptions
    };
  };
  const scanTypeField = getScanTypeField();
  return <Stack direction='row' sx={{
    gap: '8px',
    alignItems: 'center'
  }}>
      <Typography>{scanTypeField.displayText}</Typography>
      {isUpdating && <Loader size='medium' />}
      {itemConfigBody && !isUpdating && scanTypeField.menuOptions.length > 0 && <OptionMenu options={scanTypeField.menuOptions} />}
    </Stack>;
};